/**
 * Purchase order hooks (S3.2) — house list stack + store-first mutations.
 *
 * - `usePurchaseOrdersTable` drives the rebuilt PurchaseOrderListPage via
 *   useAntdTable (pre-first-fetch `loading || params.length === 0` guard,
 *   store echo). Search / status / approvalStatus / single-vendor filters go
 *   server-side; multi-vendor unions client-side over a mock-scale fetch so
 *   pagination totals stay correct (useSalesOrdersTable precedent).
 * - `useDemandPool` composes open SO lines − existing Allocations into the
 *   vendor×product demand rows (SCN merge key optional) with the
 *   date-window + container-CBM breaker inputs.
 * - `useVendorConfirm` is the A1/A2/A3 checkpoint orchestrator: it flips
 *   the PO via the S3.1 acknowledge endpoint (A1), mutates the confirmed
 *   OCC quantity buckets through PATCH /occ/lines/:id (recomputeOccLine runs
 *   server-side), raises short/over/delay exceptions with owner routing
 *   (late→採購 · short-ship→船務 · unpaid→財務), and appends the 黃單
 *   capture record to the poStore checkpoint log.
 * - Reads use `/api/`-prefixed cacheKeys (dynamic ones tracked through the
 *   cacheKeyRegistry); every mutation invalidates via exact-key clears.
 */

import { clearCache, useAntdTable, useRequest } from 'ahooks';
import dayjs from 'dayjs';
import { apiClient, occApi, purchaseOrderApi, salesOrderApi } from '@/services/api';
import { useProductOptions } from '@/hooks/useProducts';
import { usePoStore } from '@/stores/poStore';
import type {
  CheckpointExceptionKind,
  CheckpointExceptionOwner,
  CheckpointLineRecord,
  PoFilters,
  VendorCheckpointRecord,
} from '@/stores/poStore';
import { clearCacheWhere, trackCacheKey } from '@/utils/cacheKeyRegistry';
import type { Allocation } from '@/types/api/allocation';
import type {
  CreatePurchaseOrderRequest,
  PurchaseOrder,
  PurchaseOrderListParams,
} from '@/types/api/purchaseOrder';
import type { OccExceptionType, OccLine } from '@/types/api/occ';
import type { Product } from '@/types/api/product';
import type { SalesOrder } from '@/types/api/salesOrder';
import type {
  ReorderSuggestionListParams,
  ReorderSuggestionListResponse,
  SendToPoolRequest,
  SendToPoolResponse,
} from '@/types/api/reorder';
import type { VendorCheckpointCode } from '@/constants/ofcVocabulary';

// ---------------------------------------------------------------------------
// Cache keys (static, /api/-prefixed — house rule)
// ---------------------------------------------------------------------------

export const poDetailCacheKey = (id: string) => `/api/purchase-orders/${id}`;
const PO_OCC_BOARD_CACHE_KEY = '/api/occ/board?view=po-checkpoints';
const DEMAND_SO_CACHE_KEY = '/api/sales-orders?view=demand-pool';
const DEMAND_ALLOCATIONS_CACHE_KEY = '/api/allocations?view=demand-pool';

/** Mock-scale ceiling for the client-side union/composition fetches. */
const UNION_FETCH_PAGE_SIZE = 500;

/** Invalidate every cache a purchase-order mutation can stale (exact keys). */
export function invalidatePurchaseOrderCaches(...poIds: Array<string | undefined>) {
  const detailKeys = poIds
    .filter((id): id is string => Boolean(id))
    .map((id) => poDetailCacheKey(id));
  if (detailKeys.length > 0) clearCache(detailKeys);
  clearCacheWhere(
    (key) =>
      key.startsWith('/api/purchase-orders') ||
      key.startsWith('/api/allocations') ||
      key.startsWith('/api/occ') ||
      key.startsWith('/api/sales-orders') ||
      // A new/changed PO shifts Tier-2 qtyOpenToReceive + the open-PO dedupe
      // badge, so the derived 補貨建議 worklist must re-compute (SR2.1, D-1).
      key.startsWith('/api/reorder')
  );
  clearCache([
    '/api/sales-orders?view=product-transactions',
    '/api/purchase-orders?view=product-transactions',
  ]);
}

// ---------------------------------------------------------------------------
// List (useAntdTable — S0.3 list-kit contract)
// ---------------------------------------------------------------------------

export function usePurchaseOrdersTable(filters: PoFilters) {
  const { setPurchaseOrders, setPagination } = usePoStore();

  const needsClientFilter = filters.vendorIds.length > 1;

  const baseParams = (): PurchaseOrderListParams => ({
    search: filters.search.trim() || undefined,
    status: filters.status === 'all' ? undefined : filters.status,
    approvalStatus: filters.approvalStatus === 'all' ? undefined : filters.approvalStatus,
  });

  const { tableProps, refresh, loading, error, params } = useAntdTable(
    async ({ current, pageSize }) => {
      if (!needsClientFilter) {
        const response = await purchaseOrderApi.list({
          ...baseParams(),
          vendorId: filters.vendorIds[0],
          page: current,
          pageSize,
        });
        setPurchaseOrders(response.data);
        setPagination(response.pagination);
        return { total: response.pagination.total, list: response.data };
      }

      // Multi-vendor: union client-side over a mock-scale fetch so
      // pagination totals stay correct (useSalesOrdersTable precedent).
      const response = await purchaseOrderApi.list({
        ...baseParams(),
        page: 1,
        pageSize: UNION_FETCH_PAGE_SIZE,
      });
      const allowed = new Set(filters.vendorIds);
      const rows = response.data.filter((po) => allowed.has(po.vendorId));
      const start = (current - 1) * pageSize;
      const pageRows = rows.slice(start, start + pageSize);
      setPurchaseOrders(pageRows);
      setPagination({
        page: current,
        pageSize,
        total: rows.length,
        totalPages: Math.ceil(rows.length / pageSize) || 1,
      });
      return { total: rows.length, list: pageRows };
    },
    {
      defaultPageSize: 10,
      refreshDeps: [
        filters.search,
        filters.status,
        filters.approvalStatus,
        JSON.stringify(filters.vendorIds),
      ],
    }
  );

  return {
    tableProps,
    refresh,
    // Pre-first-fetch guard (house rule) — never flash the empty state
    isLoading: loading || params.length === 0,
    error,
  };
}

// ---------------------------------------------------------------------------
// Detail
// ---------------------------------------------------------------------------

export function usePurchaseOrder(id?: string) {
  const { data, error, loading, run } = useRequest(() => purchaseOrderApi.getById(id!), {
    ready: !!id,
    refreshDeps: [id],
    cacheKey: id ? trackCacheKey(poDetailCacheKey(id)) : undefined,
  });

  return {
    purchaseOrder: data?.data,
    error,
    isLoading: loading,
    /** Invalidate + re-run (never bare ahooks refresh in components). */
    refresh: () => {
      if (id) invalidatePurchaseOrderCaches(id);
      return run();
    },
  };
}

/**
 * OCC lines carrying this PO's confirmed quantity buckets (the checkpoint
 * read model until S3.3's FulfillmentLine projections land). Board read is
 * shared-cached; the PO filter is client-side.
 */
export function useOccLinesForPo(poId?: string) {
  const { data, error, loading, run } = useRequest(() => occApi.board(), {
    ready: !!poId,
    refreshDeps: [poId],
    cacheKey: PO_OCC_BOARD_CACHE_KEY,
  });

  return {
    occLines: (data?.data ?? []).filter((line) => line.poId === poId),
    error,
    isLoading: loading,
    refresh: () => run(),
  };
}

// ---------------------------------------------------------------------------
// Demand pool (open SO lines − Allocations, grouped vendor×product)
// ---------------------------------------------------------------------------

/** One SO line's still-unallocated remainder feeding the pool. */
export interface DemandMemberLine {
  soId: string;
  soNumber: string;
  soLineId: string;
  customerName: string;
  /** 預計出貨日 driving the date-window breaker hint. */
  expectedShipDate: string | null;
  qtyOrdered: number;
  qtyAllocated: number;
  openQty: number;
}

/** A vendor×product demand row (the pool's grouping unit). */
export interface DemandRow {
  key: string;
  /**
   * Which source put this row in the pool: `so` = confirmed-SO short demand
   * (this selector); `reorder_suggestion` = a 補貨建議 top-up sent to the pool
   * (SR2.1 send-to-pool / SR3.1 merge). The queue tags reorder-sourced rows.
   */
  source: 'so' | 'reorder_suggestion';
  productId: string;
  sku: string;
  productName: string;
  /** SCN link group — the OPTIONAL merge key (view grouping only). */
  scn: string | null;
  /** Preferred vendor (US-266 prefill); null = vendor must be assigned. */
  vendorId: number | null;
  vendorName: string | null;
  /** Negotiated unitCost on the preferred vendor line, when on file. */
  unitCost: number | null;
  currency: string | null;
  openQty: number;
  /** m³ per unit (product dims), null when dims are missing. */
  unitCbm: number | null;
  /** openQty × unitCbm, null when dims are missing. */
  totalCbm: number | null;
  members: DemandMemberLine[];
}

/** Statuses whose lines feed the pool (post-confirm, pre-close). */
const DEMAND_SO_STATUSES: ReadonlyArray<SalesOrder['status']> = ['confirmed', 'converted_to_po'];

/**
 * Pure composition: open SO lines minus allocated qty, grouped
 * vendor×product. Exported for reuse/tests.
 */
export function buildDemandRows(
  salesOrders: SalesOrder[],
  allocations: Allocation[],
  products: Product[]
): DemandRow[] {
  const allocatedBySoLine = new Map<string, number>();
  for (const alloc of allocations) {
    allocatedBySoLine.set(
      alloc.soLineId,
      (allocatedBySoLine.get(alloc.soLineId) ?? 0) + alloc.qty
    );
  }
  const productById = new Map(products.map((product) => [product.id, product]));

  const rows = new Map<string, DemandRow>();
  for (const so of salesOrders) {
    if (!DEMAND_SO_STATUSES.includes(so.status)) continue;
    // pending_approval blocks fulfillment — those lines never enter the pool.
    if (so.priceStatus !== 'ok') continue;
    for (const line of so.lines) {
      const allocated = allocatedBySoLine.get(line.id) ?? 0;
      const openQty = line.qty - allocated;
      if (openQty <= 0) continue;

      const product = productById.get(line.productId);
      const vendorId = product?.preferredVendorId ?? null;
      const key = `${vendorId ?? 'unassigned'}|${line.productId}`;
      const primaryLine = product?.vendorLines?.find((vendor) => vendor.isPrimary);
      const unitCbm = product?.cbm ?? null;

      const existing = rows.get(key);
      const member: DemandMemberLine = {
        soId: so.id,
        soNumber: so.soNumber,
        soLineId: line.id,
        customerName: so.customerName,
        expectedShipDate: so.expectedShipDate,
        qtyOrdered: line.qty,
        qtyAllocated: allocated,
        openQty,
      };
      if (existing) {
        existing.openQty += openQty;
        existing.totalCbm = unitCbm != null ? (existing.totalCbm ?? 0) + openQty * unitCbm : existing.totalCbm;
        existing.members.push(member);
      } else {
        rows.set(key, {
          key,
          source: 'so',
          productId: line.productId,
          sku: line.sku,
          productName: line.productName,
          scn: product?.scn ?? null,
          vendorId,
          vendorName: product?.preferredVendorName ?? null,
          unitCost: primaryLine?.unitCost ?? null,
          currency: primaryLine?.currency ?? null,
          openQty,
          unitCbm,
          totalCbm: unitCbm != null ? openQty * unitCbm : null,
          members: [member],
        });
      }
    }
  }

  return [...rows.values()].sort(
    (a, b) =>
      (a.vendorName ?? '￿').localeCompare(b.vendorName ?? '￿') ||
      a.sku.localeCompare(b.sku)
  );
}

export function useDemandPool() {
  const sos = useRequest(() => salesOrderApi.list({ pageSize: UNION_FETCH_PAGE_SIZE }), {
    cacheKey: DEMAND_SO_CACHE_KEY,
  });
  const allocs = useRequest(() => purchaseOrderApi.listAllocations(), {
    cacheKey: DEMAND_ALLOCATIONS_CACHE_KEY,
  });
  const prods = useProductOptions('all');

  const isLoading = sos.loading || allocs.loading || prods.isLoading;
  const error = sos.error || allocs.error || prods.error;

  const rows =
    sos.data && allocs.data && prods.data
      ? buildDemandRows(sos.data.data, allocs.data.data, prods.data.data)
      : [];

  return {
    rows,
    isLoading,
    error,
    refresh: () => {
      clearCache([DEMAND_SO_CACHE_KEY, DEMAND_ALLOCATIONS_CACHE_KEY]);
      sos.run();
      allocs.run();
      prods.refresh();
    },
  };
}

// ---------------------------------------------------------------------------
// Reorder suggestions (SR2.1) — advisory worklist + send-to-pool top-up
// ---------------------------------------------------------------------------

/** Request path (axios prepends the `/api` baseURL). */
const REORDER_SUGGESTIONS_PATH = '/reorder/suggestions';
/** Cache-key prefix (house rule: `/api/`-prefixed, tracked in the registry). */
const REORDER_SUGGESTIONS_CACHE_PREFIX = '/api/reorder/suggestions';

function reorderSuggestionsCacheKey(params: ReorderSuggestionListParams): string {
  return `${REORDER_SUGGESTIONS_CACHE_PREFIX}?${JSON.stringify(params)}`;
}

/**
 * The 補貨建議 worklist (SR2.1). Transactional data — a short `staleTime` keeps it
 * live without hammering the read-through; the suggestion is derived-never-stored
 * server-side, so this only reads (send-to-pool + PO create invalidate it). The
 * surface is gated by FeatureGate `reorder.suggestions` at the SR3.1 call site.
 */
export function useReorderSuggestions(params: ReorderSuggestionListParams = {}) {
  const { data, error, loading, run } = useRequest(
    async () => {
      const response = await apiClient.get<ReorderSuggestionListResponse>(
        REORDER_SUGGESTIONS_PATH,
        { params }
      );
      return response.data;
    },
    {
      cacheKey: trackCacheKey(reorderSuggestionsCacheKey(params)),
      staleTime: 15_000,
      refreshDeps: [JSON.stringify(params)],
    }
  );

  return {
    suggestions: data?.data ?? [],
    syncedAt: data?.syncedAt ?? null,
    computedAt: data?.computedAt ?? null,
    error,
    isLoading: loading,
    refresh: () => {
      clearCacheWhere((key) => key.startsWith(REORDER_SUGGESTIONS_CACHE_PREFIX));
      return run();
    },
  };
}

/**
 * Send selected suggestions to the demand pool as top-up rows (SR2.1). Store-
 * first: invalidate the reorder worklist AND the demand-pool caches so both
 * re-derive with the reorder-sourced quantity folded in.
 */
export function useSendToPool() {
  const sendToPool = async (productIds: string[]): Promise<SendToPoolResponse> => {
    const body: SendToPoolRequest = { productIds };
    const response = await apiClient.post<SendToPoolResponse>(
      `${REORDER_SUGGESTIONS_PATH}/send-to-pool`,
      body
    );
    clearCacheWhere((key) => key.startsWith(REORDER_SUGGESTIONS_CACHE_PREFIX));
    clearCache([DEMAND_SO_CACHE_KEY, DEMAND_ALLOCATIONS_CACHE_KEY]);
    return response.data;
  };

  return { sendToPool };
}

// ---------------------------------------------------------------------------
// Breaker hints (date-window + container-CBM) — pure, page-level consumers
// ---------------------------------------------------------------------------

/** Selected lines spreading 預計出貨日 wider than this suggest a split. */
export const DEMAND_DATE_WINDOW_DAYS = 14;
/** Usable capacity (m³) — 20GP / 40HQ (裝櫃批次 planning heuristics). */
export const CONTAINER_CBM_20GP = 28;
export const CONTAINER_CBM_40HQ = 68;

export interface DemandBreakerHints {
  /** Days between earliest and latest 預計出貨日 (null = no dated members). */
  dateSpanDays: number | null;
  dateWindowExceeded: boolean;
  totalCbm: number;
  /** Rows lacking dims — their CBM is unknown and excluded from the total. */
  rowsMissingCbm: number;
  cbmExceeds40Hq: boolean;
}

export function computeDemandBreakerHints(rows: DemandRow[]): DemandBreakerHints {
  const dates = rows
    .flatMap((row) => row.members.map((member) => member.expectedShipDate))
    .filter((date): date is string => Boolean(date))
    .sort();
  const dateSpanDays =
    dates.length >= 2 ? dayjs(dates[dates.length - 1]).diff(dayjs(dates[0]), 'day') : null;
  const totalCbm = rows.reduce((sum, row) => sum + (row.totalCbm ?? 0), 0);
  return {
    dateSpanDays,
    dateWindowExceeded: dateSpanDays != null && dateSpanDays > DEMAND_DATE_WINDOW_DAYS,
    totalCbm: Math.round(totalCbm * 100) / 100,
    rowsMissingCbm: rows.filter((row) => row.totalCbm == null).length,
    cbmExceeds40Hq: totalCbm > CONTAINER_CBM_40HQ,
  };
}

// ---------------------------------------------------------------------------
// Mutations (store-first: update Zustand echo + exact-key clearCache)
// ---------------------------------------------------------------------------

export function usePurchaseOrderMutations() {
  const { upsertPurchaseOrderInList } = usePoStore();

  const createPurchaseOrder = async (body: CreatePurchaseOrderRequest): Promise<PurchaseOrder> => {
    const response = await purchaseOrderApi.create(body);
    upsertPurchaseOrderInList(response.data);
    invalidatePurchaseOrderCaches(response.data.id);
    return response.data;
  };

  const sendPurchaseOrder = async (id: string): Promise<PurchaseOrder> => {
    const response = await purchaseOrderApi.send(id);
    upsertPurchaseOrderInList(response.data);
    invalidatePurchaseOrderCaches(id);
    return response.data;
  };

  /** >NT$200k manager release/reject — ReasonRequiredModal contract. */
  const decidePurchaseOrder = async (
    id: string,
    decision: 'approve' | 'reject',
    actor: string,
    reason: string
  ): Promise<PurchaseOrder> => {
    const response = await purchaseOrderApi.decide(id, { decision, actor, reason });
    upsertPurchaseOrderInList(response.data);
    invalidatePurchaseOrderCaches(id);
    return response.data;
  };

  return { createPurchaseOrder, sendPurchaseOrder, decidePurchaseOrder };
}

// ---------------------------------------------------------------------------
// Vendor confirmation checkpoints A1/A2/A3 (the S3.2 drawer's engine)
// ---------------------------------------------------------------------------

/**
 * Owner routing (SO→PO board): late→採購 · short-ship→船務 · unpaid→財務.
 * A1/A2 shortfalls are back-orders the buyer chases (採購); only a short at
 * the A3 裝櫃實際 loading checkpoint routes to 船務. `unpaid` is listed for
 * the routing map's completeness — W8 AP raises it, not this drawer.
 */
export const CHECKPOINT_EXCEPTION_OWNER: Record<
  'late' | 'short_confirm' | 'over' | 'short_load' | 'unpaid',
  CheckpointExceptionOwner
> = {
  late: 'procurement',
  short_confirm: 'procurement',
  over: 'procurement',
  short_load: 'shipping',
  unpaid: 'finance',
};

export interface CheckpointLineInput {
  poLineId: string;
  qtyConfirmed: number;
  deliveryDate: string | null;
  priceAdjusted: boolean;
  boxCount: number | null;
  packaging: string | null;
}

export interface ConfirmCheckpointInput {
  checkpoint: VendorCheckpointCode;
  note: string;
  lines: CheckpointLineInput[];
}

/** Bucket field a checkpoint mutates on the OCC allocation rows. */
const CHECKPOINT_BUCKET_FIELD: Record<
  VendorCheckpointCode,
  'qtyEstimated' | 'qtyNoticeConfirmed' | 'qtyLoaded'
> = {
  A1: 'qtyEstimated',
  A2: 'qtyNoticeConfirmed',
  A3: 'qtyLoaded',
};

/** Exception classification for one confirmed line (pure — vitest target). */
export function classifyCheckpointException(
  checkpoint: VendorCheckpointCode,
  qtyBaseline: number,
  qtyConfirmed: number,
  deliveryDate: string | null,
  referenceDate: string | null
): CheckpointLineRecord['exception'] {
  if (qtyConfirmed < qtyBaseline) {
    const kind: CheckpointExceptionKind = 'short';
    return {
      kind,
      owner:
        checkpoint === 'A3'
          ? CHECKPOINT_EXCEPTION_OWNER.short_load
          : CHECKPOINT_EXCEPTION_OWNER.short_confirm,
      gap: qtyBaseline - qtyConfirmed,
    };
  }
  if (qtyConfirmed > qtyBaseline) {
    return { kind: 'over', owner: CHECKPOINT_EXCEPTION_OWNER.over, gap: qtyConfirmed - qtyBaseline };
  }
  if (deliveryDate && referenceDate && deliveryDate > referenceDate) {
    return { kind: 'delay', owner: CHECKPOINT_EXCEPTION_OWNER.late, gap: 0 };
  }
  return null;
}

/** OCC exception enum per checkpoint exception kind. */
function occExceptionType(
  checkpoint: VendorCheckpointCode,
  kind: CheckpointExceptionKind
): OccExceptionType {
  if (kind === 'delay') return 'late';
  if (kind === 'short' && checkpoint === 'A3') return 'short_load';
  return 'qty_mismatch';
}

/** Baseline a checkpoint confirms against: PO qty; A3 against A2 (fallback A1/PO). */
export function checkpointBaselineQty(
  checkpoint: VendorCheckpointCode,
  poLineQty: number,
  occAllocation?: { qtyEstimated: number; qtyNoticeConfirmed: number }
): number {
  if (checkpoint === 'A3') {
    if (occAllocation?.qtyNoticeConfirmed) return occAllocation.qtyNoticeConfirmed;
    if (occAllocation?.qtyEstimated) return occAllocation.qtyEstimated;
  }
  return poLineQty;
}

let checkpointSeq = 0;

export function useVendorConfirm() {
  const { appendCheckpointRecord } = usePoStore();

  /**
   * Apply one checkpoint: A1 flips the PO sent→vendor_confirmed via the S3.1
   * acknowledge endpoint; every checkpoint mutates the confirmed OCC buckets
   * (PATCH /occ/lines/:id → recomputeOccLine); short/over/delay lines raise
   * an OCC 訂單異常 with owner routing; the 黃單 capture is appended to the
   * client-side checkpoint log (see poStore header).
   */
  const confirmCheckpoint = async (
    po: PurchaseOrder,
    occLines: OccLine[],
    input: ConfirmCheckpointInput
  ): Promise<VendorCheckpointRecord> => {
    const now = new Date().toISOString();
    const bucketField = CHECKPOINT_BUCKET_FIELD[input.checkpoint];

    // -- classify every line against its baseline --------------------------
    const lineRecords: CheckpointLineRecord[] = input.lines.map((lineInput) => {
      const poLine = po.lines.find((line) => line.id === lineInput.poLineId);
      const occAllocation = occLines
        .flatMap((line) => line.allocations)
        .find((alloc) => alloc.poLineId === lineInput.poLineId);
      const qtyBaseline = checkpointBaselineQty(
        input.checkpoint,
        poLine?.qty ?? lineInput.qtyConfirmed,
        occAllocation
      );
      const referenceDate = poLine?.expectedDate ?? po.expectedDeliveryDate ?? null;
      return {
        poLineId: lineInput.poLineId,
        sku: poLine?.sku ?? lineInput.poLineId,
        productName: poLine?.productName ?? '',
        qtyBaseline,
        qtyConfirmed: lineInput.qtyConfirmed,
        deliveryDate: lineInput.deliveryDate,
        priceAdjusted: lineInput.priceAdjusted,
        boxCount: lineInput.boxCount,
        packaging: lineInput.packaging,
        exception: classifyCheckpointException(
          input.checkpoint,
          qtyBaseline,
          lineInput.qtyConfirmed,
          lineInput.deliveryDate,
          referenceDate
        ),
      };
    });

    // -- A1 status flip (sent → vendor_confirmed) --------------------------
    if (input.checkpoint === 'A1' && po.status === 'sent') {
      const summary = lineRecords
        .map((record) => `${record.sku} ${record.qtyConfirmed}/${record.qtyBaseline}`)
        .join(', ');
      await purchaseOrderApi.acknowledge(
        po.id,
        input.note ? `${summary} — ${input.note}` : summary
      );
    }

    // -- mutate confirmed buckets + raise exceptions on the OCC lines ------
    for (const occLine of occLines) {
      const touched = occLine.allocations.filter((alloc) =>
        lineRecords.some((record) => record.poLineId === alloc.poLineId)
      );
      if (touched.length === 0) continue;

      const nextAllocations = occLine.allocations.map((alloc) => {
        const record = lineRecords.find((line) => line.poLineId === alloc.poLineId);
        return record ? { ...alloc, [bucketField]: record.qtyConfirmed, updatedAt: now } : alloc;
      });

      const exceptionRecord = lineRecords.find(
        (record) =>
          record.exception &&
          occLine.allocations.some((alloc) => alloc.poLineId === record.poLineId)
      );

      const patch: Partial<OccLine> = { allocations: nextAllocations };
      if (exceptionRecord?.exception) {
        const { kind, owner, gap } = exceptionRecord.exception;
        patch.node = 'exception';
        patch.exceptionType = occExceptionType(input.checkpoint, kind);
        patch.exceptionOwner = owner;
        patch.exceptionNote = [
          `${input.checkpoint} ${exceptionRecord.sku} ${exceptionRecord.qtyConfirmed}/${exceptionRecord.qtyBaseline}`,
          kind === 'delay'
            ? `delivery ${exceptionRecord.deliveryDate ?? '?'}`
            : `${kind} ${gap}`,
          input.note,
        ]
          .filter(Boolean)
          .join(' — ');
        patch.exceptionDueAt = dayjs(now).add(48, 'hour').toISOString();
      } else {
        patch.node = input.checkpoint === 'A3' ? 'in_transit' : 'vendor_confirm';
      }

      await occApi.updateLine(occLine.id, patch);
    }

    // -- append the 黃單 capture record ------------------------------------
    const record: VendorCheckpointRecord = {
      id: `cp-${Date.now()}-${++checkpointSeq}`,
      poId: po.id,
      poNumber: po.poNumber,
      checkpoint: input.checkpoint,
      at: now,
      note: input.note.trim() || null,
      lines: lineRecords,
    };
    appendCheckpointRecord(record);

    invalidatePurchaseOrderCaches(po.id);
    clearCache([PO_OCC_BOARD_CACHE_KEY]);
    return record;
  };

  return { confirmCheckpoint };
}
